import { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  IconButton,
  Backdrop,
  CircularProgress,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  ChevronLeft,
  ChevronRight,
  Add,
  Delete,
} from '@mui/icons-material';
import type { Task } from '../../types';
import type { TaskStatus } from './DateView';
import { apiRequest } from '../../utils/api';
import { formatDateLocal } from '../../utils/dateUtils';
import TaskModal from '../../components/TaskModal';

// Room and JobRoomOption interfaces are now defined in TaskModal component

interface MonthViewProps {
  tasks: Task[];
  onTasksChange: (tasks: Task[]) => void;
  onEditTask: (task: Task) => void;
  onDeleteTask: (taskId: number) => void;
  onMoveTask: (taskId: number, newStatus: TaskStatus) => void;
  setSavingTask?: (saving: boolean) => void;
}

export default function MonthView({ 
  tasks: _tasks, 
  onTasksChange, 
  onDeleteTask,
  setSavingTask
}: MonthViewProps) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [tasks, setTasks] = useState<Task[]>([]);
  const [loadingTasks, setLoadingTasks] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingTask, setEditingTask] = useState<Task | null>(null);
  const [, setDraggedTaskId] = useState<number | null>(null);
  const [dragOverDate, setDragOverDate] = useState<Date | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [loadingDragDrop, setLoadingDragDrop] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [taskToDelete, setTaskToDelete] = useState<number | null>(null);
  const [initialTaskDate, setInitialTaskDate] = useState<Date | undefined>(undefined);

  // Get first day of month and number of days
  const getMonthData = (date: Date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();
    
    return { firstDay, lastDay, daysInMonth, startingDayOfWeek, year, month };
  };

  // All modal-related data loading is now handled by TaskModal component
  // Removed unused useEffect hooks that referenced removed state variables
  /* All the useEffect hooks below are commented out because TaskModal handles all modal data loading
  useEffect(() => {
    if (!editingTask || !openDialog) {
      return;
    }

    let isCancelled = false;

    const loadAllEditingData = async () => {
      setLoadingJobs(true);
      try {
        const taskType = editingTask.taskType || '';
        
        // Build query params based on task type
        const params = new URLSearchParams({
          showActive: 'true',
          limit: '1000',
        });
        
        // Filter jobs by status based on task type
        let jobStatus: string | null = null;
        if (taskType) {
          switch (taskType) {
            case 'Measure':
              jobStatus = 'Measure';
              break;
            case 'Shop Drawing':
              jobStatus = 'Shop Drawing';
              break;
            case 'Fabrication':
              jobStatus = 'Fabrication';
              break;
            case 'Install':
              jobStatus = 'Install';
              break;
          }
          if (jobStatus) {
            params.append('status', jobStatus);
          }
        }
        
        // Fetch current job first (always include it)
        let jobsData: Job[] = [];
        if (editingTask.jobId) {
          try {
            const currentJobResponse = await apiRequest(`/jobs/${editingTask.jobId}`, { method: 'GET' });
            const currentJobData = currentJobResponse.data;
            const mappedCurrentJob: Job = {
              id: currentJobData.id,
              job: currentJobData.job_number,
              customer: currentJobData.customer_name || '',
              customerAddress: currentJobData.customer_address || '',
              jobAddress: currentJobData.job_address,
              apartment: currentJobData.apartment || '',
              status: (currentJobData.status || 'Estimate') as Job['status'],
              quoteStatus: currentJobData.quote_status as Job['quoteStatus'],
              payStatus: currentJobData.pay_status as Job['payStatus'],
              appointmentDate: currentJobData.appointment_date
                ? new Date(currentJobData.appointment_date).toLocaleDateString('en-US')
                : '',
              startTime: currentJobData.start_time || '',
              endTime: currentJobData.end_time || '',
              dueDate: currentJobData.due_date
                ? new Date(currentJobData.due_date).toLocaleDateString('en-US')
                : '',
              userId: currentJobData.user_id ?? undefined,
              active: currentJobData.active !== undefined ? currentJobData.active : true,
            };
            jobsData.push(mappedCurrentJob);
          } catch (error) {
            console.error(`Error loading current job ${editingTask.jobId}:`, error);
          }
        }
        
        // Fetch other jobs with same status (if task type exists)
        if (jobStatus && !isCancelled) {
          try {
            const jobsResponse = await apiRequest(`/jobs?${params.toString()}`, { method: 'GET' });
            const result = jobsResponse.data;
            const rawData = result?.data || result || [];
            
            const additionalJobs: Job[] = Array.isArray(rawData) ? rawData.map((j: any) => ({
              id: j.id,
              job: j.job_number,
              customer: j.customer_name || '',
              customerAddress: j.customer_address || '',
              jobAddress: j.job_address,
              apartment: j.apartment || '',
              status: (j.status || 'Estimate') as Job['status'],
              quoteStatus: j.quote_status as Job['quoteStatus'],
              payStatus: j.pay_status as Job['payStatus'],
              appointmentDate: j.appointment_date
                ? new Date(j.appointment_date).toLocaleDateString('en-US')
                : '',
              startTime: j.start_time || '',
              endTime: j.end_time || '',
              dueDate: j.due_date
                ? new Date(j.due_date).toLocaleDateString('en-US')
                : '',
              userId: j.user_id ?? undefined,
              active: j.active !== undefined ? j.active : true,
            })) : [];
            
            // Merge jobs, avoiding duplicates
            additionalJobs.forEach(job => {
              if (!jobsData.some(j => j.id === job.id)) {
                jobsData.push(job);
              }
            });
          } catch (error) {
            console.error('Error loading additional jobs:', error);
          }
        }
        
        if (isCancelled) return;
        setJobs(jobsData);

        // Fetch rooms for each job
        const roomsMap = new Map<number, Room[]>();
        const options: JobRoomOption[] = [];

        for (const job of jobsData) {
          if (isCancelled) return;
          try {
            const roomsResponse = await apiRequest(`/products/rooms/job/${job.id}`, { method: 'GET' });
            const roomsData = (roomsResponse.data || []) as Room[];
            
            // When editing, include the current room even if it's not active
            let roomsToShow: Room[];
            if (editingTask && editingTask.jobId === job.id && editingTask.roomId) {
              const activeRooms = roomsData.filter(room => room.status === 'active');
              const currentRoom = roomsData.find(room => room.id === editingTask.roomId);
              roomsToShow = [...activeRooms];
              if (currentRoom && !activeRooms.some(r => r.id === currentRoom.id)) {
                roomsToShow.push(currentRoom);
              }
            } else {
              roomsToShow = roomsData.filter(room => room.status === 'active');
            }
            
            roomsMap.set(job.id, roomsToShow);

            roomsToShow.forEach(room => {
              options.push({
                jobId: job.id,
                jobNumber: job.job || `Job #${job.id}`,
                roomId: room.id,
                roomName: room.room_name,
                displayText: `${job.job || `Job #${job.id}`} - ${room.room_name}`,
              });
            });
          } catch (error) {
            console.error(`Error loading rooms for job ${job.id}:`, error);
          }
        }

        if (isCancelled) return;
        setRooms(roomsMap);
        setJobRoomOptions(options);
        
        // Load product details if room is selected
        if (editingTask && editingTask.jobId && editingTask.roomId) {
          const selectedOption = options.find(
            opt => opt.jobId === editingTask.jobId && opt.roomId === editingTask.roomId
          );
          
          if (selectedOption && !isCancelled) {
            try {
              const productsResponse = await apiRequest(`/products/job/${editingTask.jobId}`, { method: 'GET' });
              const productsData = (productsResponse.data || []) as Product[];
              
              const filteredProducts = productsData.filter(
                product => product.room?.toLowerCase().trim() === selectedOption.roomName.toLowerCase().trim()
              );
              
              const productDetailsWithProducts: Array<{
                detail: ProductDetail;
                product: Product;
              }> = [];
              
              filteredProducts.forEach(product => {
                if (product.details && product.details.length > 0) {
                  product.details.forEach(detail => {
                    productDetailsWithProducts.push({
                      detail,
                      product,
                    });
                  });
                }
              });
              
              if (!isCancelled && editingTask) {
                setRoomProductDetails(productDetailsWithProducts);
                
                // Restore product detail status updates from editing task
                if (editingTask.productDetailStatusUpdates) {
                  const updatesMap = new Map<number, string>();
                  Object.entries(editingTask.productDetailStatusUpdates).forEach(([detailId, status]) => {
                    updatesMap.set(Number(detailId), status);
                  });
                  setProductDetailStatusUpdates(updatesMap);
                }
              }
            } catch (error) {
              console.error('Error loading product details:', error);
            }
          }
        }
        
        // Load work location
        if (editingTask && editingTask.id && !isCancelled) {
          try {
            const response = await apiRequest(`/work-locations?taskId=${editingTask.id}`, { method: 'GET' });
            const locations = response.data || [];
            if (locations.length > 0 && !isCancelled) {
              const location = locations[0];
              setWorkLocationId(location.id);
              setHasWorkLocation(true);
              setWorkLocationAddress(location.location || '');
              setWorkLocationLatitude(location.latitude ? Number(location.latitude) : null);
              setWorkLocationLongitude(location.longitude ? Number(location.longitude) : null);
            } else if (!isCancelled) {
              setWorkLocationId(null);
              setHasWorkLocation(false);
              setWorkLocationAddress('');
              setWorkLocationLatitude(null);
              setWorkLocationLongitude(null);
            }
          } catch (error) {
            console.error('Error loading work location:', error);
          }
        }
      } catch (error) {
        console.error('Error loading editing data:', error);
      } finally {
        if (!isCancelled) {
          setLoadingJobs(false);
        }
      }
    };

    loadAllEditingData();
    
    return () => {
      isCancelled = true;
    };
  }, [editingTask?.id, openDialog]);

  // Only fetch when task type is selected (not when dialog opens, and not when editing)
  useEffect(() => {
    // Skip if editing (handled by separate effect above)
    if (editingTask) {
      return;
    }
    
    // Only load jobs when dialog is open AND task type is selected
    if (!openDialog || !selectedTaskType) {
      return;
    }

    const loadJobsAndRooms = async () => {
      setLoadingJobs(true);
      try {
        // Build query params based on selected task type
        const params = new URLSearchParams({
          showActive: 'true',
          limit: '1000',
        });
        
        // Filter jobs by status based on selected task type
        let jobStatus: string | null = null;
        switch (selectedTaskType) {
          case 'Measure':
            // For Measure tasks, show jobs in Estimate, Proposal, or Measure status
            jobStatus = 'Measure';
            break;
          case 'Shop Drawing':
            jobStatus = 'Shop Drawing';
            break;
          case 'Fabrication':
            jobStatus = 'Fabrication';
            break;
          case 'Install':
            jobStatus = 'Install';
            break;
        }
        if (jobStatus) {
          params.append('status', jobStatus);
        }
        
        // Fetch active jobs (filtered by task type if selected)
        const jobsResponse = await apiRequest(`/jobs?${params.toString()}`, { method: 'GET' });
        const result = jobsResponse.data;
        const rawData = result?.data || result || [];
        
        // Map the API response to Job format
        let jobsData: Job[] = Array.isArray(rawData) ? rawData.map((j: any) => ({
          id: j.id,
          job: j.job_number,
          customer: j.customer_name || '',
          customerAddress: j.customer_address || '',
          jobAddress: j.job_address,
          apartment: j.apartment || '',
          status: (j.status || 'Estimate') as Job['status'],
          quoteStatus: j.quote_status as Job['quoteStatus'],
          payStatus: j.pay_status as Job['payStatus'],
          appointmentDate: j.appointment_date
            ? new Date(j.appointment_date).toLocaleDateString('en-US')
            : '',
          startTime: j.start_time || '',
          endTime: j.end_time || '',
          dueDate: j.due_date
            ? new Date(j.due_date).toLocaleDateString('en-US')
            : '',
          userId: j.user_id ?? undefined,
          active: j.active !== undefined ? j.active : true,
        })) : [];
        
        setJobs(jobsData);

        // Fetch rooms for each job
        const roomsMap = new Map<number, Room[]>();
        const options: JobRoomOption[] = [];

        for (const job of jobsData) {
          try {
            const roomsResponse = await apiRequest(`/products/rooms/job/${job.id}`, { method: 'GET' });
            const roomsData = (roomsResponse.data || []) as Room[];
            
            // Filter rooms by status "active" (editing is handled in separate effect)
            const roomsToShow = roomsData.filter(room => room.status === 'active');
            
            roomsMap.set(job.id, roomsToShow);

            // Create options for each room
            roomsToShow.forEach(room => {
              options.push({
                jobId: job.id,
                jobNumber: job.job || `Job #${job.id}`,
                roomId: room.id,
                roomName: room.room_name,
                displayText: `${job.job || `Job #${job.id}`} - ${room.room_name}`,
              });
            });
          } catch (error) {
            console.error(`Error loading rooms for job ${job.id}:`, error);
          }
        }

        setRooms(roomsMap);
        setJobRoomOptions(options);
      } catch (error) {
        console.error('Error loading jobs:', error);
      } finally {
        setLoadingJobs(false);
      }
    };

    loadJobsAndRooms();
  }, [selectedTaskType, editingTask]);

  // Clear job/room data when dialog closes or task type is cleared
  useEffect(() => {
    if (!openDialog || !selectedTaskType) {
      setJobRoomOptions([]);
      setRooms(new Map());
      setJobs([]);
    }
  }, [openDialog, selectedTaskType]);

  // Clear selected job/room when task type changes
  useEffect(() => {
    if (selectedTaskType) {
      setSelectedJobRoom('');
      setRoomProductDetails([]);
      setProductDetailStatusUpdates(new Map());
    }
  }, [selectedTaskType]);

  // Load users for team leader and members selection
  useEffect(() => {
    const loadUsers = async () => {
      setLoadingUsers(true);
      try {
        const usersResponse = await apiRequest('/users', { method: 'GET' });
        const usersData = (usersResponse.data || []) as User[];
        // Filter to only approved users
        const approvedUsers = usersData.filter(user => user.approved);
        setUsers(approvedUsers);
      } catch (error) {
        console.error('Error loading users:', error);
        setUsers([]);
      } finally {
        setLoadingUsers(false);
      }
    };

    loadUsers();
  }, []);

  // Load current user ID
  useEffect(() => {
    const loadCurrentUser = async () => {
      try {
        const response = await apiRequest('/users/profile/me', { method: 'GET' });
        const user = response.data as User;
        setCurrentUserId(user.id);
      } catch (error) {
        console.error('Error loading current user:', error);
      }
    };

    loadCurrentUser();
  }, []);

  // Load product details when room is selected (skip when editing - handled by editing effect)
  useEffect(() => {
    // Skip if editing (product details are loaded in the editing effect)
    if (editingTask) {
      return;
    }
    
    const loadProductDetailsForRoom = async () => {
      if (!selectedJobRoom) {
        setRoomProductDetails([]);
        setProductDetailStatusUpdates(new Map());
        return;
      }

      const [jobIdStr, roomIdStr] = selectedJobRoom.split('-');
      const jobId = parseInt(jobIdStr, 10);
      const roomId = parseInt(roomIdStr, 10);

      const selectedOption = jobRoomOptions.find(
        opt => opt.jobId === jobId && opt.roomId === roomId
      );

      if (!selectedOption) {
        setRoomProductDetails([]);
        return;
      }

      setLoadingProducts(true);
      try {
        // Fetch products for the job
        const productsResponse = await apiRequest(`/products/job/${jobId}`, { method: 'GET' });
        const productsData = (productsResponse.data || []) as Product[];
        
        // Filter products by room name
        const filteredProducts = productsData.filter(
          product => product.room?.toLowerCase().trim() === selectedOption.roomName.toLowerCase().trim()
        );
        
        // Flatten all product details with their parent product
        const productDetailsWithProducts: Array<{
          detail: ProductDetail;
          product: Product;
        }> = [];
        
        filteredProducts.forEach(product => {
          if (product.details && product.details.length > 0) {
            product.details.forEach(detail => {
              productDetailsWithProducts.push({
                detail,
                product,
              });
            });
          }
        });
        
        setRoomProductDetails(productDetailsWithProducts);
        setProductDetailStatusUpdates(new Map());
      } catch (error) {
        console.error('Error loading product details:', error);
        setRoomProductDetails([]);
      } finally {
        setLoadingProducts(false);
      }
    };

    loadProductDetailsForRoom();
  }, [selectedJobRoom, jobRoomOptions]);

  // Ensure job/room is selected when editing and options are loaded
  useEffect(() => {
    if (editingTask && editingTask.jobId && editingTask.roomId && jobRoomOptions.length > 0) {
      const jobRoomKey = `${editingTask.jobId}-${editingTask.roomId}`;
      // Only set if not already set
      if (selectedJobRoom !== jobRoomKey) {
        setSelectedJobRoom(jobRoomKey);
      }
    }
  }, [editingTask, jobRoomOptions, selectedJobRoom]);

  // Load unassigned tasks when task type is selected (for filtering job-room options)
  useEffect(() => {
    const loadUnassignedTasks = async () => {
      // Only load when dialog is open, not editing, and task type is selected
      if (!openDialog || editingTask || !selectedTaskType) {
        setAllAssignedTasks([]);
        return;
      }

      try {
        const response = await apiRequest(`/tasks/unassigned?taskType=${selectedTaskType}`, { method: 'GET' });
        const unassignedTasksData = (response.data || []) as Task[];
        setAllAssignedTasks(unassignedTasksData);
      } catch (error) {
        console.error('Error loading unassigned tasks:', error);
        setAllAssignedTasks([]);
      }
    };

    loadUnassignedTasks();
  }, [openDialog, editingTask, selectedTaskType]);
  */ 

  // Load tasks for the current month
  useEffect(() => {
    const loadTasksForMonth = async () => {
      setLoadingTasks(true);
      try {
        const { firstDay, lastDay } = getMonthData(currentMonth);
        const startDateStr = formatDateLocal(firstDay);
        const endDateStr = formatDateLocal(lastDay);
        
        const response = await apiRequest(`/tasks?startDate=${startDateStr}&endDate=${endDateStr}`, { method: 'GET' });
        const tasksData = (response.data || []) as Task[];
        // Sort tasks to ensure consistent display: multi-day tasks first, then single-day tasks
        const sortedTasks = [...tasksData].sort((a, b) => {
          // Multi-day tasks come first
          const aIsMultiDay = a.endDate && a.endDate !== null && a.endDate !== '' && 
                             a.taskDate && a.endDate.split('T')[0] !== a.taskDate.split('T')[0];
          const bIsMultiDay = b.endDate && b.endDate !== null && b.endDate !== '' && 
                             b.taskDate && b.endDate.split('T')[0] !== b.taskDate.split('T')[0];
          
          if (aIsMultiDay && !bIsMultiDay) return -1;
          if (!aIsMultiDay && bIsMultiDay) return 1;
          
          // Within same type, sort by date
          if (a.taskDate && b.taskDate) {
            return a.taskDate.localeCompare(b.taskDate);
          }
          return 0;
        });
        setTasks(sortedTasks);
        onTasksChange(sortedTasks);
      } catch (error) {
        console.error('Error loading tasks:', error);
        setTasks([]);
        onTasksChange([]);
      } finally {
        setLoadingTasks(false);
      }
    };

    loadTasksForMonth();
  }, [currentMonth, onTasksChange]);

  // Navigate to previous month
  const handlePreviousMonth = () => {
    const newMonth = new Date(currentMonth);
    newMonth.setMonth(newMonth.getMonth() - 1);
    setCurrentMonth(newMonth);
  };

  // Navigate to next month
  const handleNextMonth = () => {
    const newMonth = new Date(currentMonth);
    newMonth.setMonth(newMonth.getMonth() + 1);
    setCurrentMonth(newMonth);
  };

  // Navigate to current month
  const handleCurrentMonth = () => {
    setCurrentMonth(new Date());
  };

  // Get all tasks for a specific date (all statuses, including multi-day tasks)
  const getTasksForDate = (date: Date): Task[] => {
    const dateStr = formatDateLocal(date);
    return tasks.filter((task) => {
      if (!task.taskDate) return false;
      
      const taskDateKey = task.taskDate.split('T')[0];
      
      // Check for multi-day tasks (endDate exists and is different from taskDate)
      if (task.endDate && task.endDate !== null && task.endDate !== '') {
        const endDateKey = task.endDate.split('T')[0];
        
        // Only treat as multi-day if dates are actually different
        if (endDateKey !== taskDateKey) {
          // Check if the date falls within the task's date range
          return dateStr >= taskDateKey && dateStr <= endDateKey;
        }
      }
      
      // Single-day tasks (no endDate or endDate equals taskDate)
      return taskDateKey === dateStr;
    });
  };

  // Get assignee name for a task
  const getTaskAssignee = (task: Task): string => {
    if (task.teamLeaderName) {
      return task.teamLeaderName;
    }
    if (task.memberNames && task.memberNames.length > 0) {
      return task.memberNames[0];
    }
    return 'Unassigned';
  };

  // Format month and year
  const formatMonthYear = (date: Date) => {
    return date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  };

  // Refresh tasks after operations
  const refreshTasks = async () => {
    const { firstDay, lastDay } = getMonthData(currentMonth);
    const startDateStr = formatDateLocal(firstDay);
    const endDateStr = formatDateLocal(lastDay);
    
    try {
      const response = await apiRequest(`/tasks?startDate=${startDateStr}&endDate=${endDateStr}`, { method: 'GET' });
      const tasksData = (response.data || []) as Task[];
      setTasks(tasksData);
      onTasksChange(tasksData);
    } catch (error) {
      console.error('Error refreshing tasks:', error);
    }
  };

  // Handle deleting task - opens delete confirmation modal
  const handleDeleteTask = (taskId: number) => {
    setTaskToDelete(taskId);
    setOpenDeleteDialog(true);
  };

  // Confirm delete task
  const confirmDeleteTask = async () => {
    if (!taskToDelete) return;
    
    try {
      await onDeleteTask(taskToDelete);
      await refreshTasks();
      setOpenDeleteDialog(false);
      setTaskToDelete(null);
    } catch (error) {
      console.error('Error deleting task:', error);
      alert('Failed to delete task. Please try again.');
      setOpenDeleteDialog(false);
      setTaskToDelete(null);
    }
  };

  // Handle drag start
  const handleDragStart = (e: React.DragEvent, taskId: number) => {
    setIsDragging(true);
    setDraggedTaskId(taskId);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', taskId.toString());
    // Make the dragged element semi-transparent
    if (e.currentTarget instanceof HTMLElement) {
      e.currentTarget.style.opacity = '0.5';
    }
  };

  // Handle drag end
  const handleDragEnd = (e: React.DragEvent) => {
    setDraggedTaskId(null);
    setDragOverDate(null);
    // Restore opacity
    if (e.currentTarget instanceof HTMLElement) {
      e.currentTarget.style.opacity = '1';
    }
    // Reset dragging flag after a short delay to prevent click events
    setTimeout(() => {
      setIsDragging(false);
    }, 100);
  };

  // Handle drag over (allow drop)
  const handleDragOver = (e: React.DragEvent, date: Date) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverDate(date);
  };

  // Handle drag leave
  const handleDragLeave = (e: React.DragEvent) => {
    // Only clear dragOverDate if we're leaving the day cell itself
    const relatedTarget = e.relatedTarget as HTMLElement;
    if (!e.currentTarget.contains(relatedTarget)) {
      setDragOverDate(null);
    }
  };

  // Handle drop
  const handleDrop = async (e: React.DragEvent, targetDate: Date) => {
    e.preventDefault();
    setDragOverDate(null);
    setIsDragging(false);
    
    const taskIdStr = e.dataTransfer.getData('text/plain');
    const taskId = parseInt(taskIdStr, 10);
    
    if (isNaN(taskId)) {
      return;
    }

    const task = tasks.find(t => t.id === taskId);
    if (!task) {
      return;
    }

    // Check if the task is being dropped on a different date
    const targetDateStr = formatDateLocal(targetDate);
    const currentTaskDateStr = task.taskDate ? task.taskDate.split('T')[0] : '';
    
    if (targetDateStr === currentTaskDateStr) {
      // Same date, no need to update
      return;
    }

    setLoadingDragDrop(true);
    setSavingTask?.(true);
    try {
      // Helper function to parse date string
      const parseDateString = (dateStr: string): Date => {
        const parts = dateStr.split('T')[0].split('-');
        if (parts.length === 3) {
          return new Date(parseInt(parts[0]), parseInt(parts[1]) - 1, parseInt(parts[2]));
        }
        return new Date(dateStr);
      };

      // Check if this is a multi-day task
      const isMultiDayTask = task.endDate && 
                             task.endDate !== null && 
                             task.endDate !== '' &&
                             task.taskDate &&
                             task.endDate.split('T')[0] !== task.taskDate.split('T')[0];

      let updateData: any = { taskDate: targetDateStr };

      if (isMultiDayTask && task.taskDate && task.endDate) {
        // Calculate the duration of the task (difference in days)
        const oldStartDate = parseDateString(task.taskDate);
        const oldEndDate = parseDateString(task.endDate);
        const durationMs = oldEndDate.getTime() - oldStartDate.getTime();
        const durationDays = Math.ceil(durationMs / (1000 * 60 * 60 * 24));

        // Calculate the new end date by adding the duration to the new start date
        const newStartDate = parseDateString(targetDateStr);
        const newEndDate = new Date(newStartDate);
        newEndDate.setDate(newEndDate.getDate() + durationDays);
        
        // Format the new end date
        const newEndDateStr = formatDateLocal(newEndDate);
        
        // Update both start and end dates
        updateData.endDate = newEndDateStr;
      } else {
        // Single-day task: clear endDate if it exists
        updateData.endDate = null;
      }

      // Update the task's date(s)
      await apiRequest(`/tasks/${taskId}`, {
        method: 'PUT',
        body: JSON.stringify(updateData),
      });
      
      // Refresh tasks to show the updated date
      await refreshTasks();
    } catch (error) {
      console.error('Error updating task date:', error);
      alert('Failed to move task. Please try again.');
    } finally {
      setLoadingDragDrop(false);
      setSavingTask?.(false);
    }
  };

  // Handle adding new task
  const handleAddTask = (date?: Date) => {
    setEditingTask(null);
    setInitialTaskDate(date);
    setOpenDialog(true);
  };

  // Handle editing task
  const handleEditTaskInternal = (task: Task) => {
    setEditingTask(task);
    setOpenDialog(true);
  };


  // Handle saving task (called from TaskModal)
  const handleSaveTask = async (taskData: any) => {
    try {
      setSavingTask?.(true);
      if (editingTask) {
        // Update existing task
        const { workLocation, ...taskPayload } = taskData;
        taskPayload.hasWorkLocation = workLocation?.hasWorkLocation ?? false;
        await apiRequest(`/tasks/${editingTask.id}`, {
          method: 'PUT',
          body: JSON.stringify(taskPayload),
        });

        // Handle manual work location (backend handles geocoding when hasWorkLocation=false)
        if (workLocation?.hasWorkLocation && workLocation.address && workLocation.currentUserId) {
          if (workLocation.workLocationId) {
            try {
              await apiRequest(`/work-locations/${workLocation.workLocationId}`, {
                method: 'PUT',
                body: JSON.stringify({
                  date: workLocation.taskDate,
                  location: workLocation.address,
                  latitude: workLocation.latitude,
                  longitude: workLocation.longitude,
                  userId: workLocation.currentUserId,
                  taskId: editingTask.id,
                }),
              });
            } catch (error) {
              console.error('Error updating work location:', error);
            }
          } else {
            try {
              await apiRequest('/work-locations', {
                method: 'POST',
                body: JSON.stringify({
                  date: workLocation.taskDate,
                  location: workLocation.address,
                  latitude: workLocation.latitude,
                  longitude: workLocation.longitude,
                  userId: workLocation.currentUserId,
                  taskId: editingTask.id,
                }),
              });
            } catch (error) {
              console.error('Error creating work location:', error);
            }
          }
        }
      } else {
        // Create new task
        const { workLocation, ...taskPayload } = taskData;
        taskPayload.status = 'TO DO';
        taskPayload.hasWorkLocation = workLocation?.hasWorkLocation ?? false;

        const response = await apiRequest('/tasks', {
          method: 'POST',
          body: JSON.stringify(taskPayload),
        });

        const newTask = response.data as Task;

        // Create manual work location if specified (backend handles geocoding otherwise)
        if (workLocation?.hasWorkLocation && workLocation.address && workLocation.currentUserId) {
          try {
            await apiRequest('/work-locations', {
              method: 'POST',
              body: JSON.stringify({
                date: workLocation.taskDate,
                location: workLocation.address,
                latitude: workLocation.latitude,
                longitude: workLocation.longitude,
                userId: workLocation.currentUserId,
                taskId: newTask.id,
              }),
            });
          } catch (error) {
            console.error('Error creating work location:', error);
          }
        }
      }
      
      // Refresh tasks
      await refreshTasks();
    } catch (error) {
      console.error('Error saving task:', error);
      throw error; // Re-throw so TaskModal can handle it
    } finally {
      setSavingTask?.(false);
    }
  };



  // Check if date is today
  const isToday = (date: Date) => {
    const today = new Date();
    return (
      date.getDate() === today.getDate() &&
      date.getMonth() === today.getMonth() &&
      date.getFullYear() === today.getFullYear()
    );
  };

  // Check if date is in current month
  const isCurrentMonth = (date: Date) => {
    return (
      date.getMonth() === currentMonth.getMonth() &&
      date.getFullYear() === currentMonth.getFullYear()
    );
  };

  // Get color based on task status
  const getStatusColor = (status: TaskStatus): string => {
    switch (status) {
      case 'TO DO':
        return '#1976d2'; // Blue
      case 'IN PROGRESS':
        return '#ed6c02'; // Orange
      case 'COMPLETE':
        return '#9c27b0'; // Purple
      case 'DONE':
        return '#2e7d32'; // Green
      default:
        return '#757575'; // Gray (fallback)
    }
  };

  // Get hover color based on task status
  const getStatusHoverColor = (status: TaskStatus): string => {
    switch (status) {
      case 'TO DO':
        return '#1565c0'; // Darker blue
      case 'IN PROGRESS':
        return '#e65100'; // Darker orange
      case 'COMPLETE':
        return '#7b1fa2'; // Darker purple
      case 'DONE':
        return '#1b5e20'; // Darker green
      default:
        return '#616161'; // Darker gray (fallback)
    }
  };

  const { daysInMonth, startingDayOfWeek, year, month } = getMonthData(currentMonth);

  // Generate calendar days
  const calendarDays: (Date | null)[] = [];
  
  // Add empty cells for days before month starts
  for (let i = 0; i < startingDayOfWeek; i++) {
    calendarDays.push(null);
  }
  
  // Add days of the month
  for (let day = 1; day <= daysInMonth; day++) {
    calendarDays.push(new Date(year, month, day));
  }

  // Helper function to calculate task duration in days
  const getTaskDuration = (task: Task): number => {
    if (!task.taskDate) return 0;
    
    if (task.endDate && task.endDate !== null && task.endDate !== '') {
      const taskDateKey = task.taskDate.split('T')[0];
      const endDateKey = task.endDate.split('T')[0];
      
      if (endDateKey !== taskDateKey) {
        // Multi-day task - calculate duration
        const parseDateString = (dateStr: string): Date => {
          const parts = dateStr.split('T')[0].split('-');
          if (parts.length === 3) {
            return new Date(parseInt(parts[0]), parseInt(parts[1]) - 1, parseInt(parts[2]));
          }
          return new Date(dateStr);
        };
        
        const startDate = parseDateString(task.taskDate);
        const endDate = parseDateString(task.endDate);
        const diffTime = endDate.getTime() - startDate.getTime();
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1; // +1 to include both start and end days
        return diffDays;
      }
    }
    
    // Single-day task
    return 1;
  };

  // Get multi-day tasks (tasks with endDate that is not null or empty and different from taskDate)
  // Sort by duration (longest first)
  const multiDayTasks = tasks
    .filter(task => {
      if (!task.endDate || !task.taskDate) return false;
      if (task.endDate === null || task.endDate === '') return false;
      
      // Compare dates by their date components only (ignore time)
      const taskDateKey = task.taskDate.split('T')[0];
      const endDateKey = task.endDate.split('T')[0];
      
      // Only include if endDate is actually different from taskDate (true multi-day task)
      return endDateKey !== taskDateKey;
    })
    .sort((a, b) => {
      // Sort by duration (longest first), then by start date
      const durationA = getTaskDuration(a);
      const durationB = getTaskDuration(b);
      
      if (durationB !== durationA) {
        return durationB - durationA; // Longer tasks first
      }
      
      // If same duration, sort by start date (earlier first)
      if (a.taskDate && b.taskDate) {
        return a.taskDate.localeCompare(b.taskDate);
      }
      return 0;
    });
  
  // Calculate spanning positions for multi-day tasks
  const getTaskSpanInfo = (task: Task) => {
    if (!task.endDate || !task.taskDate) return null;
    
    // First check: compare date strings directly (fast check)
    const taskDateKey = task.taskDate.split('T')[0];
    const endDateKey = task.endDate.split('T')[0];
    
    // If start and end dates are the same, treat as single-day task (don't show as spanning bar)
    if (taskDateKey === endDateKey) return null;
    
    // Parse date strings properly (YYYY-MM-DD format) - create dates in local timezone
    const parseDateString = (dateStr: string): Date => {
      const parts = dateStr.split('T')[0].split('-');
      if (parts.length === 3) {
        // Create date in local timezone (month is 0-indexed)
        return new Date(parseInt(parts[0]), parseInt(parts[1]) - 1, parseInt(parts[2]));
      }
      return new Date(dateStr);
    };
    
    // Helper to get date components for comparison (ignores time)
    const getDateKey = (date: Date): string => {
      return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
    };
    
    const startDate = parseDateString(task.taskDate);
    const endDate = parseDateString(task.endDate);
    
    // Get date keys for comparison (double-check after parsing)
    const startDateKey = getDateKey(startDate);
    const endDateKeyParsed = getDateKey(endDate);
    
    // If start and end dates are the same after parsing, treat as single-day task
    if (startDateKey === endDateKeyParsed) return null;
    
    const monthStart = new Date(year, month, 1);
    const monthEnd = new Date(year, month + 1, 0);
    
    // Only show if task overlaps with current month
    if (endDate < monthStart || startDate > monthEnd) return null;
    
    // Find actual grid indices
    let startIndex = -1;
    let endIndex = -1;
    let spanLength = 0;
    
    for (let i = 0; i < calendarDays.length; i++) {
      const day = calendarDays[i];
      if (!day) continue;
      
      // Calendar day is already a Date object created with new Date(year, month, day)
      // Get its date key for comparison
      const dayDateKey = getDateKey(day);
      
      // Compare date strings (YYYY-MM-DD format)
      if (dayDateKey >= startDateKey && dayDateKey <= endDateKey) {
        if (startIndex === -1) startIndex = i;
        endIndex = i;
        spanLength++;
      }
    }
    
    if (startIndex === -1 || endIndex === -1) return null;
    
    return { startIndex, endIndex, spanLength };
  };

  // Helper function to calculate multi-day task stack positions (reusable)
  const calculateMultiDayTaskStackPositions = () => {
    const taskStackPositions = new Map<number, number>();
    
    const tasksOverlap = (task1: Task, task2: Task): boolean => {
      if (!task1.taskDate || !task1.endDate || !task2.taskDate || !task2.endDate) return false;
      const getDateKey = (dateStr: string): string => dateStr.split('T')[0];
      const task1Start = getDateKey(task1.taskDate);
      const task1End = getDateKey(task1.endDate);
      const task2Start = getDateKey(task2.taskDate);
      const task2End = getDateKey(task2.endDate);
      return (task1Start <= task2End && task1End >= task2Start);
    };
    
    multiDayTasks.forEach((task, index) => {
      if (!task.taskDate || !task.endDate) return;
      
      let stackIndex = 0;
      for (let i = 0; i < index; i++) {
        const otherTask = multiDayTasks[i];
        if (tasksOverlap(task, otherTask)) {
          const otherStackIndex = taskStackPositions.get(otherTask.id) || 0;
          stackIndex = Math.max(stackIndex, otherStackIndex + 1);
        }
      }
      
      taskStackPositions.set(task.id, stackIndex);
    });
    
    return taskStackPositions;
  };

  // Calculate multi-day task stack positions once
  const multiDayTaskStackPositions = calculateMultiDayTaskStackPositions();

  // Helper function to get multi-day task height for a specific day
  const getMultiDayTasksHeightForDay = (dayIndex: number): number => {
    const date = calendarDays[dayIndex];
    if (!date) return isMobile ? 28 : 32; // dateNumberArea
    
    const dateRow = Math.floor(dayIndex / 7);
    const dateStr = formatDateLocal(date);
    const dateNumberArea = isMobile ? 28 : 32;
    const taskBarHeight = isMobile ? 40 : 52; // Height of each task bar (smaller on mobile)
    const taskBarGap = 4;
    const gapAfterMultiDay = 4;
    
    let maxStackIndex = -1;
    multiDayTasks.forEach(task => {
      const spanInfo = getTaskSpanInfo(task);
      if (!spanInfo) return;
      
      const taskRow = Math.floor(spanInfo.startIndex / 7);
      if (taskRow === dateRow) {
        const taskDateKey = task.taskDate?.split('T')[0] || '';
        const endDateKey = task.endDate?.split('T')[0] || '';
        if (dateStr >= taskDateKey && dateStr <= endDateKey) {
          const stackIndex = multiDayTaskStackPositions.get(task.id) || 0;
          maxStackIndex = Math.max(maxStackIndex, stackIndex);
        }
      }
    });
    
    return maxStackIndex >= 0 
      ? dateNumberArea + ((maxStackIndex + 1) * taskBarHeight) + (maxStackIndex * taskBarGap) + gapAfterMultiDay
      : dateNumberArea;
  };


  return (
    <Box>
      {/* Loading Backdrop */}
      <Backdrop
        sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={loadingTasks || loadingDragDrop}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
          <CircularProgress color="inherit" />
          <Typography variant="h6">
            {loadingDragDrop ? 'Moving task...' : 'Loading tasks...'}
          </Typography>
        </Box>
      </Backdrop>

      {/* Month Header */}
      <Paper 
        sx={{ 
          p: { xs: 2, sm: 3 }, 
          mb: { xs: 2, sm: 3 },
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white',
          borderRadius: 2,
          boxShadow: 3,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1, sm: 2 }, flex: 1 }}>
            <IconButton 
              onClick={handlePreviousMonth} 
              sx={{ color: 'white', p: { xs: 0.75, sm: 1 } }}
              size={isMobile ? 'small' : 'medium'}
            >
              <ChevronLeft />
            </IconButton>
            <Box sx={{ textAlign: 'center', flex: 1 }}>
              <Typography 
                variant={isMobile ? 'h6' : 'h5'} 
                sx={{ fontWeight: 700, fontSize: { xs: '1.1rem', sm: '1.5rem' } }}
              >
                {formatMonthYear(currentMonth)}
              </Typography>
              <Button 
                variant="outlined" 
                size={isMobile ? 'small' : 'small'}
                onClick={handleCurrentMonth}
                sx={{ 
                  mt: { xs: 0.5, sm: 1 }, 
                  color: 'white', 
                  borderColor: 'white',
                  fontSize: { xs: '0.7rem', sm: '0.875rem' },
                  px: { xs: 1, sm: 2 },
                  py: { xs: 0.25, sm: 0.5 },
                  '&:hover': { borderColor: 'white', bgcolor: 'rgba(255,255,255,0.1)' }
                }}
              >
                Today
              </Button>
            </Box>
            <IconButton 
              onClick={handleNextMonth} 
              sx={{ color: 'white', p: { xs: 0.75, sm: 1 } }}
              size={isMobile ? 'small' : 'medium'}
            >
              <ChevronRight />
            </IconButton>
          </Box>
        </Box>
      </Paper>

      {/* Color Legend */}
      <Paper sx={{ p: { xs: 1.5, sm: 2 }, mb: { xs: 1.5, sm: 2 } }}>
        <Typography 
          variant="subtitle2" 
          sx={{ 
            mb: { xs: 1, sm: 1.5 }, 
            fontWeight: 600, 
            color: 'text.secondary',
            fontSize: { xs: '0.75rem', sm: '0.875rem' }
          }}
        >
          Task Status Colors:
        </Typography>
        <Box sx={{ display: 'flex', gap: { xs: 1, sm: 2 }, flexWrap: 'wrap' }}>
          <Chip
            label="TO DO"
            size={isMobile ? 'small' : 'medium'}
            sx={{
              bgcolor: '#1976d2',
              color: 'white',
              fontWeight: 500,
              fontSize: { xs: '0.7rem', sm: '0.875rem' },
              height: { xs: 24, sm: 32 },
            }}
          />
          <Chip
            label="IN PROGRESS"
            size={isMobile ? 'small' : 'medium'}
            sx={{
              bgcolor: '#ed6c02',
              color: 'white',
              fontWeight: 500,
              fontSize: { xs: '0.7rem', sm: '0.875rem' },
              height: { xs: 24, sm: 32 },
            }}
          />
          <Chip
            label="COMPLETE"
            size={isMobile ? 'small' : 'medium'}
            sx={{
              bgcolor: '#9c27b0',
              color: 'white',
              fontWeight: 500,
              fontSize: { xs: '0.7rem', sm: '0.875rem' },
              height: { xs: 24, sm: 32 },
            }}
          />
          <Chip
            label="DONE"
            size={isMobile ? 'small' : 'medium'}
            sx={{
              bgcolor: '#2e7d32',
              color: 'white',
              fontWeight: 500,
              fontSize: { xs: '0.7rem', sm: '0.875rem' },
              height: { xs: 24, sm: 32 },
            }}
          />
        </Box>
      </Paper>

      {/* Calendar Grid */}
      <Paper sx={{ p: { xs: 1, sm: 2 }, overflow: { xs: 'auto', sm: 'visible' } }}>
        {/* Day Headers */}
        <Box 
          sx={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(7, 1fr)', 
            gap: { xs: 0.5, sm: 1 }, 
            mb: { xs: 0.5, sm: 1 },
            minWidth: { xs: '600px', sm: 'auto' }, // Enable horizontal scroll on mobile
          }}
        >
          {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day) => (
            <Box key={day}>
              <Typography 
                variant="subtitle2" 
                sx={{ 
                  textAlign: 'center', 
                  fontWeight: 600,
                  color: 'text.secondary',
                  textTransform: 'uppercase',
                  fontSize: { xs: '0.65rem', sm: '0.75rem' },
                }}
              >
                {isMobile ? day.substring(0, 1) : day}
              </Typography>
            </Box>
          ))}
        </Box>

        {/* Calendar Days Container - Relative positioning for spanning bars */}
        <Box 
          sx={{ 
            position: 'relative', 
            display: 'grid', 
            gridTemplateColumns: 'repeat(7, 1fr)', 
            gap: { xs: 0.5, sm: 1 },
            minWidth: { xs: '600px', sm: 'auto' }, // Enable horizontal scroll on mobile
          }}
        >
          {/* Multi-day task spanning bars */}
          {(() => {
            // Render tasks with stacking (using pre-calculated stack positions)
            const renderedTasks: (JSX.Element | null)[] = [];
            multiDayTasks.forEach((task) => {
              const spanInfo = getTaskSpanInfo(task);
              if (!spanInfo) return;
              
              const assignee = getTaskAssignee(task);
              const truncatedTitle = isMobile 
                ? (task.title.length > 20 ? task.title.substring(0, 20) + '...' : task.title)
                : (task.title.length > 30 ? task.title.substring(0, 30) + '...' : task.title);
              const statusColor = getStatusColor(task.status);
              const statusHoverColor = getStatusHoverColor(task.status);
              
              // Calculate position and width using percentage-based approach
              // Grid has 7 columns with responsive gaps (gap: 0.5 = 4px on mobile, gap: 1 = 8px on desktop)
              const gapSize = isMobile ? 4 : 8; // Responsive gap size
              // Calculate column positions (accounting for empty cells at start)
              const startColumn = spanInfo.startIndex % 7;
              const endColumn = spanInfo.endIndex % 7;
              const numColumns = endColumn - startColumn + 1;
              
              // For CSS Grid with gap: 1 (8px), the grid container has:
              // - 7 columns of equal width
              // - 6 gaps of 8px each between columns
              // Each column takes up (100% - 6*8px) / 7 of the width
              // But since we're using percentage + fixed pixels, we need to be careful
              
              // Left position: percentage of start column + gaps before it
              const leftPercent = (startColumn / 7) * 100;
              const leftGapOffset = startColumn * gapSize;
              
              // Width calculation: 
              // Simpler approach - use percentage and add minimal gap offset
              // The percentage already accounts for most of the width
              const widthPercent = (numColumns / 7) * 100;
              // Only add gaps between the columns we're spanning (not full gap size to avoid overflow)
              const widthGapOffset = leftGapOffset * (1.5); // Use half gap size to prevent overflow
              // Calculate which row this task should be on (0-indexed)
              // Each row has 7 columns, so row = Math.floor(startIndex / 7)
              const row = Math.floor(spanInfo.startIndex / 7);
              
              // Calculate top position based on the row
              // CSS Grid automatically handles cell positioning, but we need to calculate
              // the absolute position. Each cell has minHeight: 150px (desktop) / 100px (mobile) and there's a gap between cells.
              // We position the bar below the date number area (32px from top of each cell)
              const dayCellMinHeight = isMobile ? 100 : 150; // Responsive minHeight
              const dateNumberArea = isMobile ? 28 : 32; // Space for date number and add button
              const rowGap = isMobile ? 4 : 8; // gap: 0.5 (4px) on mobile, 1 (8px) on desktop
              const taskBarHeight = isMobile ? 40 : 52; // Height of each task bar (smaller on mobile)
              const taskBarGap = 4; // Gap between stacked task bars
              
              // Get stack position for this task (0 = first/top, 1 = second, etc.)
              // Since tasks are sorted by duration (longest first), stackIndex 0 is the longest task
              const stackIndex = multiDayTaskStackPositions.get(task.id) || 0;
              
              // Calculate top offset: base row position + stacking offset
              // Multi-day tasks are positioned starting from the date number area (longest tasks first)
              const baseTopOffset = dateNumberArea + (row * (dayCellMinHeight + rowGap));
              
              // Multi-day tasks are positioned at the top (longest first), so no offset needed for single-day tasks
              const topOffset = baseTopOffset + (stackIndex * (taskBarHeight + taskBarGap));
              
              renderedTasks.push(
              <Box
                key={`span-${task.id}`}
                draggable
                onDragStart={(e) => handleDragStart(e, task.id)}
                onDragEnd={handleDragEnd}
                onClick={() => {
                  if (!isDragging) {
                    handleEditTaskInternal(task);
                  }
                }}
                sx={{
                  position: 'absolute',
                  left: `calc(${leftPercent}% + ${leftGapOffset}px)`,
                  top: `${topOffset}px`, // Position based on actual calendar row
                  // Width calculation: 
                  // The grid has 7 columns with 6 gaps (8px each = 48px total)
                  // Each column width = (100% - 48px) / 7
                  // For numColumns, width = numColumns * column width + (numColumns - 1) * gap
                  // Simplified: use percentage for columns, add only gaps between spanned columns
                  width: `calc(${widthPercent}% - ${widthGapOffset}px)`,
                  height: { xs: '40px', sm: '52px' },
                  bgcolor: statusColor,
                  color: 'white',
                  borderRadius: '4px',
                  p: { xs: 0.5, sm: 0.75 },
                  px: { xs: 0.75, sm: 1 },
                  cursor: 'pointer',
                  zIndex: 100, // Above day cells
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.12)',
                  '&:hover': {
                    bgcolor: statusHoverColor,
                    boxShadow: '0 2px 6px rgba(0,0,0,0.16)',
                  },
                  '&:active': {
                    cursor: 'grabbing',
                  },
                }}
              >
                <Box sx={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>
                  <Typography
                    variant="caption"
                    sx={{
                      display: 'block',
                      fontSize: { xs: '0.65rem', sm: '0.75rem' },
                      fontWeight: 500,
                      lineHeight: 1.3,
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      mb: { xs: 0.15, sm: 0.25 },
                    }}
                  >
                    {truncatedTitle}
                  </Typography>
                  {task.taskType && (
                    <Typography
                      variant="caption"
                      sx={{
                        display: 'block',
                        fontSize: { xs: '0.55rem', sm: '0.65rem' },
                        opacity: 0.85,
                        lineHeight: 1.2,
                        fontWeight: 600,
                        mb: { xs: 0.1, sm: 0.15 },
                      }}
                    >
                      {task.taskType}
                    </Typography>
                  )}
                  <Typography
                    variant="caption"
                    sx={{
                      display: 'block',
                      fontSize: { xs: '0.6rem', sm: '0.7rem' },
                      opacity: 0.95,
                      lineHeight: 1.2,
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                  >
                    {assignee}
                  </Typography>
                </Box>
                <IconButton
                  size={isMobile ? 'small' : 'small'}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDeleteTask(task.id);
                  }}
                  sx={{
                    p: { xs: 0.25, sm: 0.5 },
                    color: 'white',
                    ml: { xs: 0.25, sm: 0.5 },
                    minWidth: { xs: '20px', sm: '24px' },
                    width: { xs: '20px', sm: '24px' },
                    height: { xs: '20px', sm: '24px' },
                    '&:hover': {
                      bgcolor: 'rgba(255, 255, 255, 0.2)',
                    },
                  }}
                >
                  <Delete fontSize={isMobile ? 'small' : 'small'} />
                </IconButton>
              </Box>
              );
            });
            
            return renderedTasks;
          })()}

          {/* Calendar Days */}
          {calendarDays.map((date, index) => {
            if (!date) {
              return (
                <Paper 
                  key={`empty-${index}`} 
                  sx={{ 
                    p: { xs: 0.5, sm: 1 }, 
                    minHeight: { xs: 100, sm: 150 }, 
                    bgcolor: 'grey.50' 
                  }} 
                />
              );
            }

            // Filter out multi-day tasks from day tasks (they're shown as spanning bars)
            // Only show single-day tasks in the day cell
            const dayTasks = getTasksForDate(date).filter(task => {
              // If task has no endDate, it's a single-day task
              if (!task.endDate || task.endDate === null || task.endDate === '') return true;
              if (!task.taskDate) return false;
              
              // Compare date keys to determine if it's truly a single-day task
              const taskDateKey = task.taskDate.split('T')[0];
              const endDateKey = task.endDate.split('T')[0];
              
              // Only show if start and end dates are the same (single-day task)
              return taskDateKey === endDateKey;
            });
            const dayIsToday = isToday(date);
            const dayIsCurrentMonth = isCurrentMonth(date);

            const isDragOver = dragOverDate && dragOverDate.toISOString() === date.toISOString();

            return (
              <Paper
                key={date.toISOString()}
                onDragOver={(e) => handleDragOver(e, date)}
                onDragLeave={handleDragLeave}
                onDrop={(e) => handleDrop(e, date)}
                sx={{
                  p: { xs: 0.5, sm: 1 },
                  minHeight: { xs: 100, sm: 150 },
                  height: '100%',
                  border: dayIsToday ? 2 : 1,
                  borderColor: isDragOver 
                    ? 'primary.main' 
                    : dayIsToday 
                      ? 'primary.main' 
                      : 'divider',
                  borderWidth: isDragOver ? 2 : (dayIsToday ? 2 : 1),
                  bgcolor: isDragOver
                    ? '#e3f2fd' // Light blue background when dragging over
                    : dayIsToday 
                      ? '#fff9c4' // Light yellow background for today
                      : dayIsCurrentMonth 
                        ? 'background.paper' 
                        : 'grey.50',
                  position: 'relative',
                  transition: 'all 0.2s ease',
                  zIndex: 1, // Below spanning bars
                  '&:hover': {
                    boxShadow: 2,
                  },
                }}
              >
                {/* Date number and Add button in top-right corner */}
                <Box 
                  sx={{ 
                    position: 'absolute', 
                    top: { xs: 2, sm: 4 }, 
                    right: { xs: 2, sm: 4 }, 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: { xs: 0.25, sm: 0.5 } 
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: dayIsToday ? 700 : 600,
                      color: dayIsToday ? 'primary.main' : dayIsCurrentMonth ? 'text.primary' : 'text.secondary',
                      fontSize: { xs: '0.75rem', sm: '0.875rem' },
                    }}
                  >
                    {date.getDate()}
                  </Typography>
                  {dayIsCurrentMonth && (
                    <IconButton
                      size={isMobile ? 'small' : 'small'}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleAddTask(date);
                      }}
                      sx={{
                        p: { xs: 0.25, sm: 0.25 },
                        color: 'primary.main',
                        minWidth: { xs: '28px', sm: '32px' },
                        width: { xs: '28px', sm: '32px' },
                        height: { xs: '28px', sm: '32px' },
                        '&:hover': {
                          bgcolor: 'primary.light',
                          color: 'white',
                        },
                      }}
                    >
                      <Add fontSize={isMobile ? 'small' : 'small'} />
                    </IconButton>
                  )}
                </Box>

                {/* Task List - Only single-day tasks here (multi-day shown as spanning bars above) */}
                {/* Position single-day tasks below multi-day tasks */}
                <Box sx={{ 
                  display: 'flex', 
                  flexDirection: 'column', 
                  gap: { xs: 0.5, sm: 0.75 }, 
                  mt: `${getMultiDayTasksHeightForDay(index)}px` 
                }}>
                  {dayTasks
                    .sort((a, b) => {
                      // Sort single-day tasks by title for consistency
                      if (a.title && b.title) {
                        return a.title.localeCompare(b.title);
                      }
                      return 0;
                    })
                    .map((task) => {
                    const assignee = getTaskAssignee(task);
                    const truncatedTitle = isMobile 
                      ? (task.title.length > 15 ? task.title.substring(0, 15) + '...' : task.title)
                      : (task.title.length > 25 ? task.title.substring(0, 25) + '...' : task.title);
                    const statusColor = getStatusColor(task.status);
                    const statusHoverColor = getStatusHoverColor(task.status);
                    
                    return (
                      <Box
                        key={task.id}
                        draggable
                        onDragStart={(e) => handleDragStart(e, task.id)}
                        onDragEnd={handleDragEnd}
                        sx={{
                          p: { xs: 0.5, sm: 0.75 },
                          bgcolor: statusColor,
                          color: 'white',
                          borderRadius: 1,
                          position: 'relative',
                          cursor: 'grab',
                          '&:hover': {
                            bgcolor: statusHoverColor,
                          },
                          '&:active': {
                            cursor: 'grabbing',
                          },
                        }}
                      >
                        <Box
                          onClick={(_e) => {
                            // Prevent click if we just finished dragging
                            if (!isDragging) {
                              handleEditTaskInternal(task);
                            }
                          }}
                          sx={{
                            cursor: 'pointer',
                            pr: { xs: 2, sm: 2.5 },
                          }}
                        >
                          <Typography
                            variant="caption"
                            sx={{
                              display: 'block',
                              fontSize: { xs: '0.65rem', sm: '0.7rem' },
                              fontWeight: 500,
                              lineHeight: 1.2,
                              mb: 0.25,
                            }}
                          >
                            {truncatedTitle}
                          </Typography>
                          {task.taskType && (
                            <Typography
                              variant="caption"
                              sx={{
                                display: 'block',
                                fontSize: { xs: '0.55rem', sm: '0.6rem' },
                                opacity: 0.85,
                                lineHeight: 1.2,
                                fontWeight: 600,
                                mb: 0.15,
                              }}
                            >
                              {task.taskType}
                            </Typography>
                          )}
                          <Typography
                            variant="caption"
                            sx={{
                              display: 'block',
                              fontSize: { xs: '0.6rem', sm: '0.65rem' },
                              opacity: 0.9,
                              lineHeight: 1.2,
                            }}
                          >
                            {assignee}
                          </Typography>
                        </Box>
                        <IconButton
                          size={isMobile ? 'small' : 'small'}
                          onClick={(_e) => {
                            _e.stopPropagation();
                            handleDeleteTask(task.id);
                          }}
                          sx={{
                            position: 'absolute',
                            top: { xs: 1, sm: 2 },
                            right: { xs: 1, sm: 2 },
                            p: { xs: 0.25, sm: 0.25 },
                            color: 'white',
                            minWidth: { xs: '24px', sm: '28px' },
                            width: { xs: '24px', sm: '28px' },
                            height: { xs: '24px', sm: '28px' },
                            '&:hover': {
                              bgcolor: 'rgba(255, 255, 255, 0.2)',
                            },
                          }}
                        >
                          <Delete fontSize={isMobile ? 'small' : 'small'} />
                        </IconButton>
                      </Box>
                    );
                  })}
                </Box>
              </Paper>
            );
          })}
        </Box>
      </Paper>

      {/* Add/Edit Task Dialog */}
      <TaskModal
        open={openDialog}
        editingTask={editingTask}
        initialDate={initialTaskDate}
        onClose={() => {
          setOpenDialog(false);
          setEditingTask(null);
          setInitialTaskDate(undefined);
        }}
        onSave={handleSaveTask}
        onRefreshTasks={refreshTasks}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={openDeleteDialog}
        onClose={() => {
          setOpenDeleteDialog(false);
          setTaskToDelete(null);
        }}
      >
        <DialogTitle>Delete Task</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this task? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setOpenDeleteDialog(false);
            setTaskToDelete(null);
          }}>
            Cancel
          </Button>
          <Button onClick={confirmDeleteTask} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
